/**
 * @fileoverview Editor do horário de trabalho de um atendente (aba "Horários"
 * do UserEditorModal). Modelo "Expediente + Almoço":
 *   - Por grupo de dias: um Expediente (início–fim) + um Almoço OPCIONAL.
 *   - O almoço é descontado do ART individual do atendente — por baixo, vira o
 *     vão entre duas faixas (ex.: 09:00–18:00 com almoço 12:00–13:00 é salvo
 *     como 09:00–12:00 + 13:00–18:00).
 *
 * CONTROLADO sobre `groups`: o estado vive no modal (sobrevive à troca de abas);
 * aqui só renderiza e dispara `onChange(novosGroups)`. Sem botão de salvar.
 * `showErrors` (do modal, após uma tentativa de salvar) força a exibição dos
 * erros; antes disso, cada campo só acusa erro depois de tocado (blur).
 *
 * Helpers de conversão/validação em ./helpers (re-exportados aqui para manter
 * a assinatura estável usada pelo modal). Estilos em ./styles.
 */
import React, { useMemo, useState } from "react";
import { Chip, IconButton, Tooltip } from "@material-ui/core";
import TextField from "@material-ui/core/TextField";
import NumberFormat from "react-number-format";
import { Plus, Trash2, Clock } from "lucide-react";

import useStyles from "./styles";
import {
  ALL_DAYS,
  ALL_WEEKDAYS_EN,
  WEEKDAYS,
  computeGroupErrors,
  isTimeComplete,
  makeId,
  normalizeTime,
  toMinutes,
} from "./helpers";

export {
  scheduleValueToGroups,
  scheduleGroupsToValue,
  scheduleGroupsHaveErrors,
} from "./helpers";

const TimeInput = ({ className, value, onChange, onBlur, placeholder }) => (
  <NumberFormat
    value={value}
    onValueChange={({ formattedValue }) => onChange(formattedValue)}
    onBlur={onBlur}
    variant="outlined"
    margin="dense"
    customInput={TextField}
    format="##:##"
    className={className}
    placeholder={placeholder}
    inputProps={{ inputMode: "numeric" }}
  />
);

const UserScheduleField = ({ groups = [], onChange, showErrors = false }) => {
  const classes = useStyles();
  const [touched, setTouched] = useState({});
  const groupErrors = useMemo(() => computeGroupErrors(groups), [groups]);
  const usedDays = useMemo(() => groups.flatMap((g) => g.days), [groups]);
  const availableDays = ALL_WEEKDAYS_EN.filter((d) => !usedDays.includes(d));

  const markTouched = (groupId, field) =>
    setTouched((t) => ({ ...t, [`${groupId}:${field}`]: true }));
  const isTouched = (groupId, field) => !!touched[`${groupId}:${field}`];

  const patchGroup = (groupId, patch) =>
    onChange(groups.map((g) => (g.id === groupId ? { ...g, ...patch } : g)));

  const toggleDay = (groupId, weekdayEn) => {
    // Um dia pertence a um único grupo (um expediente). Se já está em outro
    // grupo, o chip vem desabilitado — aqui só alterna dentro deste grupo.
    markTouched(groupId, "days");
    onChange(
      groups.map((g) => {
        if (g.id !== groupId) return g;
        const has = g.days.includes(weekdayEn);
        return {
          ...g,
          days: has
            ? g.days.filter((d) => d !== weekdayEn)
            : [...g.days, weekdayEn],
        };
      })
    );
  };

  // Preset = os dias do preset que ainda não estão em OUTRO grupo. Mesma regra
  // dos chips: não "rouba" dia de outro horário (evita deixar um card vazio).
  const applyPreset = (groupId, preset) => {
    const otherUsed = groups
      .filter((g) => g.id !== groupId)
      .flatMap((g) => g.days);
    const days = preset.filter((d) => !otherUsed.includes(d));
    // Preset sem dia livre não pode esvaziar o card: mantém os dias atuais.
    if (!days.length) return;
    markTouched(groupId, "days");
    patchGroup(groupId, { days });
  };

  const removeGroup = (groupId) =>
    onChange(groups.filter((g) => g.id !== groupId));

  const addGroup = () =>
    onChange([
      ...groups,
      {
        id: makeId(),
        days: WEEKDAYS.filter((d) => availableDays.includes(d)),
        startTime: "",
        endTime: "",
        lunchStart: "",
        lunchEnd: "",
      },
    ]);

  // Campo em erro visual: só depois de tocado (ou após tentar salvar), e só
  // quando o valor está completo e inválido, ou vazio numa tentativa de salvar.
  const fieldInvalid = (group, field, validator) => {
    const raw = normalizeTime(group[field]);
    const visible = showErrors || isTouched(group.id, field);
    if (!visible) return false;
    if (!raw) return showErrors && validator.requiredOnSubmit;
    if (!isTimeComplete(raw)) return true;
    return !validator.ok(raw);
  };

  return (
    <div className={classes.root}>
      <div className={classes.hint}>
        <Clock size={16} strokeWidth={1.85} />
        <span>
          Defina o <strong>expediente</strong> do atendente. O <strong>almoço</strong>{" "}
          (opcional) é descontado do tempo médio de resposta individual dele no
          Dashboard — ele não é penalizado pelo intervalo.
          <br />
          Requer <strong>Gerenciamento de Expediente = Empresa</strong> e o horário
          comercial configurado em Configurações &gt; Horários (o ART geral continua
          nele; este horário refina só o ranking do atendente). Vale para todo o
          período consultado, inclusive dias passados, e para todas as empresas em
          que o usuário atua.
        </span>
      </div>

      {groups.length === 0 && (
        <div className={classes.empty}>
          Sem horário próprio — o atendente usa o <strong>horário comercial da
          empresa</strong>.<br />
          Adicione um horário (com almoço opcional) para definir um horário
          individual.
        </div>
      )}

      {groups.map((group) => {
        const error = groupErrors[group.id];
        const groupTouched = ["days", "startTime", "endTime", "lunchStart", "lunchEnd"].some(
          (f) => isTouched(group.id, f)
        );
        const showGroupError = !!error && (showErrors || groupTouched);
        const otherUsedDays = groups
          .filter((g) => g.id !== group.id)
          .flatMap((g) => g.days);
        const s = toMinutes(normalizeTime(group.startTime));
        const e = toMinutes(normalizeTime(group.endTime));
        const expValidator = {
          requiredOnSubmit: true,
          ok: (v) => toMinutes(v) !== null && (s === null || e === null || s < e),
        };
        const lunchValidator = {
          requiredOnSubmit: false,
          ok: (v) => toMinutes(v) !== null,
        };
        const timeFieldClass = (field, validator) =>
          `${classes.timeField} ${
            fieldInvalid(group, field, validator) ? classes.timeFieldError : ""
          }`;
        return (
          <div
            key={group.id}
            className={`${classes.card} ${showGroupError ? classes.cardError : ""}`}
          >
            <div className={classes.header}>
              <div className={classes.quickActions}>
                <button
                  type="button"
                  className={classes.quickBtn}
                  onClick={() => applyPreset(group.id, WEEKDAYS)}
                  title="Segunda a sexta (dias ainda livres)"
                >
                  Dias úteis
                </button>
                <button
                  type="button"
                  className={classes.quickBtn}
                  onClick={() => applyPreset(group.id, ALL_WEEKDAYS_EN)}
                  title="Todos os dias da semana (dias ainda livres)"
                >
                  Todos os dias
                </button>
              </div>
              <Tooltip title="Remover horário">
                <IconButton
                  size="small"
                  className={classes.deleteBtn}
                  onClick={() => removeGroup(group.id)}
                >
                  <Trash2 size={18} />
                </IconButton>
              </Tooltip>
            </div>

            <div className={classes.daysRow}>
              {ALL_DAYS.map((d) => {
                const on = group.days.includes(d.weekdayEn);
                const disabled = otherUsedDays.includes(d.weekdayEn);
                const chip = (
                  <Chip
                    label={d.short}
                    size="small"
                    disabled={disabled}
                    className={`${classes.dayChip} ${
                      disabled
                        ? classes.dayChipDisabled
                        : on
                          ? classes.dayChipOn
                          : classes.dayChipOff
                    }`}
                    onClick={() => !disabled && toggleDay(group.id, d.weekdayEn)}
                  />
                );
                return disabled ? (
                  <Tooltip key={d.weekdayEn} title="Já usado em outro horário">
                    <span>{chip}</span>
                  </Tooltip>
                ) : (
                  <React.Fragment key={d.weekdayEn}>{chip}</React.Fragment>
                );
              })}
            </div>

            <div className={classes.fieldLabel}>Expediente</div>
            <div className={classes.timeRow}>
              <TimeInput
                className={timeFieldClass("startTime", expValidator)}
                value={group.startTime}
                onChange={(v) => patchGroup(group.id, { startTime: v })}
                onBlur={() => markTouched(group.id, "startTime")}
                placeholder="09:00"
              />
              <span className={classes.sep}>–</span>
              <TimeInput
                className={timeFieldClass("endTime", expValidator)}
                value={group.endTime}
                onChange={(v) => patchGroup(group.id, { endTime: v })}
                onBlur={() => markTouched(group.id, "endTime")}
                placeholder="18:00"
              />
            </div>

            <div className={classes.fieldLabel}>
              Almoço <span className={classes.fieldLabelOpt}>(opcional)</span>
            </div>
            <div className={classes.timeRow}>
              <TimeInput
                className={timeFieldClass("lunchStart", lunchValidator)}
                value={group.lunchStart || ""}
                onChange={(v) => patchGroup(group.id, { lunchStart: v })}
                onBlur={() => markTouched(group.id, "lunchStart")}
                placeholder="12:00"
              />
              <span className={classes.sep}>–</span>
              <TimeInput
                className={timeFieldClass("lunchEnd", lunchValidator)}
                value={group.lunchEnd || ""}
                onChange={(v) => patchGroup(group.id, { lunchEnd: v })}
                onBlur={() => markTouched(group.id, "lunchEnd")}
                placeholder="13:00"
              />
            </div>

            {showGroupError && <div className={classes.errorText}>{error}</div>}
          </div>
        );
      })}

      {availableDays.length > 0 && (
        <button type="button" className={classes.addBtn} onClick={addGroup}>
          <Plus size={18} />
          Adicionar horário
        </button>
      )}
    </div>
  );
};

export default UserScheduleField;
